import React, { useMemo, useState } from 'react';
import { Info, Shield, ExternalLink, Rocket, FileText, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import AboutModal from '../Common/AboutModal';
import { useSettings, getLaunchOnStartupErrorMessage } from '../../contexts/SettingsContext';

const LEGAL_LINKS = [
  {
    href: 'https://notesplus.bosph.net/privacy/',
    icon: Shield,
    labelKey: 'generalSection.privacyLabel',
    descKey: 'generalSection.privacyDescription',
  },
  {
    href: 'https://notesplus.bosph.net/terms/',
    icon: FileText,
    labelKey: 'generalSection.termsLabel',
    descKey: 'generalSection.termsDescription',
  },
  {
    href: 'https://notesplus.bosph.net/community-guidelines/',
    icon: Users,
    labelKey: 'generalSection.communityLabel',
    descKey: 'generalSection.communityDescription',
  },
];

/** Packaged non-Store Electron only — web / Store / unpackaged never show this control.
 *  Store auto-start uses AppX windows.startupTask (manifest), not setLoginItemSettings. */
const useShowLaunchOnStartup = () =>
  useMemo(() => {
    if (typeof window === 'undefined' || !window.api) return false;
    if (window.api.supportsLaunchOnStartup === true) return true;
    return (
      !!window.api.isPackaged &&
      !window.api.isWindowsStore &&
      (window.api.platform === 'win32' || window.api.platform === 'darwin')
    );
  }, []);

const GeneralSettingsSection = () => {
  const { preferences, setLaunchOnStartup, launchOnStartupBusy } = useSettings();
  const [showAboutModal, setShowAboutModal] = useState(false);
  const { t } = useTranslation('settings');
  const showLaunchOnStartup = useShowLaunchOnStartup();

  const handleLaunchOnStartupChange = async (event) => {
    const next = event.target.checked;
    if (!setLaunchOnStartup) return;
    const result = await setLaunchOnStartup(next);
    if (result?.ok) return;

    if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
      window.dispatchEvent(
        new CustomEvent('notesplus-show-toast', {
          detail: {
            type: 'error',
            title: t('generalSection.launchOnStartupErrorTitle'),
            message: getLaunchOnStartupErrorMessage(result?.error),
          },
        })
      );
    }
  };

  return (
    <>
      <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-8">
        {t('generalSection.title')}
      </h1>

      <div className="border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden">
        {showLaunchOnStartup && (
          <div className="flex items-center justify-between w-full p-4 bg-white dark:bg-[var(--color-bg-base)] border-b border-slate-100 dark:border-slate-800/50 transition-colors">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100/50 dark:bg-blue-500/10 flex items-center justify-center">
                <Rocket className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="font-medium text-slate-900 dark:text-slate-200">
                  {t('generalSection.launchOnStartupLabel')}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                  {t('generalSection.launchOnStartupDescription')}
                </p>
              </div>
            </div>
            <label
              className={`relative inline-flex items-center ${launchOnStartupBusy ? 'cursor-wait opacity-60' : 'cursor-pointer'}`}
            >
              <input
                type="checkbox"
                className="sr-only peer"
                checked={!!preferences.launchOnStartup}
                disabled={launchOnStartupBusy}
                onChange={handleLaunchOnStartupChange}
              />
              <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
        )}

        <button
          type="button"
          onClick={() => setShowAboutModal(true)}
          className="flex items-center justify-between w-full p-4 bg-white dark:bg-[var(--color-bg-base)] border-b border-slate-100 dark:border-slate-800/50 hover:bg-slate-50 dark:hover:bg-[var(--color-bg-muted)] transition-colors text-left"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-slate-100 dark:bg-[var(--color-bg-muted)] flex items-center justify-center">
              <Info className="w-5 h-5 text-slate-600 dark:text-slate-400" />
            </div>
            <div>
              <p className="font-medium text-slate-900 dark:text-slate-200">{t('generalSection.aboutLabel')}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                {t('generalSection.aboutDescription')}
              </p>
            </div>
          </div>
        </button>

        {LEGAL_LINKS.map((item, index) => {
          const Icon = item.icon;
          const isLast = index === LEGAL_LINKS.length - 1;
          return (
            <a
              key={item.href}
              href={item.href}
              target="_blank"
              rel="noopener noreferrer"
              className={`flex items-center justify-between w-full p-4 bg-white dark:bg-[var(--color-bg-base)] hover:bg-slate-50 dark:hover:bg-[var(--color-bg-muted)] transition-colors text-left ${
                isLast ? '' : 'border-b border-slate-100 dark:border-slate-800/50'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-slate-100 dark:bg-[var(--color-bg-muted)] flex items-center justify-center">
                  <Icon className="w-5 h-5 text-slate-600 dark:text-slate-400" />
                </div>
                <div>
                  <p className="font-medium text-slate-900 dark:text-slate-200">{t(item.labelKey)}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{t(item.descKey)}</p>
                </div>
              </div>
              <ExternalLink className="w-4 h-4 text-slate-400 dark:text-slate-500 flex-shrink-0" />
            </a>
          );
        })}
      </div>

      <AboutModal isOpen={showAboutModal} onClose={() => setShowAboutModal(false)} />
    </>
  );
};

export default GeneralSettingsSection;
